import { Grid, Radio, RadioGroup, useEventCallback } from '@mui/material';
import { memo, useCallback, useEffect } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { DatePicker } from '@mui/lab';
import { FormControlLabel } from '@mui/material';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import { FormControl } from '@mui/material';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import { DateTime } from 'luxon';

interface ReconciliationDataProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  controllTypeSeparation: (typeSeparation?: string) => void;
  configuration?: string;
}

const ReconciliationDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  controllTypeSeparation,
}: ReconciliationDataProps): React.ReactElement => {
  useEffect(() => {
    controllTypeSeparation(values?.typeSeparation);
  }, [values?.typeSeparation]);

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values || {}).foreign,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChange('foreign', {
        ...(valuesNew || {}),
      });
    },
    [values, onChange]
  );

  const handleTypeSeparation = useEventCallback((item?: ItemVM) => {
    onChange('typeSeparation', item?.id?.toString());
  });

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data celebrazione *"
          aria-label="Data celebrazione"
          value={values?.dateMarriage || null}
          onChange={(date) => {
            onChange('dateMarriage', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateMarriage"
              fullWidth
              helperText={errors?.dateMarriage}
              error={touched?.dateMarriage && Boolean(errors?.dateMarriage)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChanges({
                ...(values || {}),
                isForeign: !values?.isForeign,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onChangeItalian}
          onChanges={onChangesItalian}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality="Comune di celebrazione"
          labelProvince="Provincia"
          required
          fromDate={values?.dataStart || DateTime.now()}
        />
      ) : (
        <Foreign
          disabledComponent={disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          labelState="Stato di celebrazione"
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          isWithLocationForeign
        />
      )}
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Celebrato da *"
          aria-label="Celebrato da"
          id="celebratedBy"
          name="celebratedBy"
          fullWidth
          disabled={disabled}
          helperText={errors?.celebratedBy}
          error={touched?.celebratedBy && Boolean(errors?.celebratedBy)}
          type="text"
          variant="outlined"
          value={values?.celebratedBy || ''}
          onChange={(event) => {
            onChange('celebratedBy', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ListAnsc
          label="Tipo separazione"
          idDominio="104"
          value={values?.typeSeparation || ''}
          onChange={handleTypeSeparation}
          disabled={disabled}
          error={Boolean(errors?.typeSeparation) && touched?.typeSeparation}
          withoutId={['4']}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data riconciliazione *"
          aria-label="Data riconciliazione"
          value={values?.dateReconciliation || null}
          onChange={(date) => {
            onChange('dateReconciliation', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateReconciliation"
              fullWidth
              helperText={errors?.dateReconciliation}
              error={touched?.dateReconciliation && Boolean(errors?.dateReconciliation)}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

ReconciliationDataComponent.displayName = 'Form Reconciliation Data component';

const ReconciliationData = memo(ReconciliationDataComponent);

export default ReconciliationData;
